import { FormattedMessage } from 'react-intl';
import RoadmapIcon from '@atlaskit/icon/core/roadmap';
import TeamsIcon from '@atlaskit/icon/core/teams';
import WorkItemsIcon from '@atlaskit/icon/core/work-items';
import CheckCircleIcon from '@atlaskit/icon/core/check-circle';
import AutomationIcon from '@atlaskit/icon/core/automation';
import AiGenerativeTextSummaryIcon from '@atlaskit/icon/core/ai-generative-text-summary';
import RovoIcon from '@atlaskit/icon-lab/core/rovo';
import WorkItemAddIcon from '@atlaskit/icon-lab/core/work-item-add';
import { type RecommendedFeature, type RecommendedEdition, type IconComponent } from './types';

type FeatureItem = {
	id: string;
	Icon: IconComponent;
	message: React.ReactElement;
};

type ModalVariantContent = {
	HeadingMessage: React.FC<{ count: number }>;
	BodyMessage: React.FC<{ trialLength: number; edition: RecommendedEdition }>;
	features: ReadonlyArray<FeatureItem>;
};

// ─── Plans ───────────────────────────────────────────────────────────────────

function PlansHeading({ count }: { count: number }) {
	return (
		<FormattedMessage
			id="growth-luna.jira-twcg-810-upgrade-free.modal.plans.heading"
			defaultMessage="Keep your {count} projects on track"
			description="Modal heading for the Plans variation of the upgrade modal."
			values={{ count }}
		/>
	);
}

function PlansBody({ trialLength, edition }: { trialLength: number; edition: RecommendedEdition }) {
	return (
		<FormattedMessage
			id="growth-luna.jira-twcg-810-upgrade-free.modal.plans.body"
			defaultMessage="See progress across your team's work in one view. Try Plans free for {trialLength} days with {edition, select, premium {Premium} standard {Standard} other {Standard}}."
			description="Modal body text for the Plans variation of the upgrade modal."
			values={{ trialLength, edition }}
		/>
	);
}

// ─── Automations ─────────────────────────────────────────────────────────────

function AutomationsHeading({ count }: { count: number }) {
	return (
		<FormattedMessage
			id="growth-luna.jira-twcg-810-upgrade-free.modal.automations.heading"
			defaultMessage="Keep your {count} rules running"
			description="Modal heading for the Automations variation of the upgrade modal."
			values={{ count }}
		/>
	);
}

function AutomationsBody({
	trialLength,
	edition,
}: {
	trialLength: number;
	edition: RecommendedEdition;
}) {
	return (
		<FormattedMessage
			id="growth-luna.jira-twcg-810-upgrade-free.modal.automations.body"
			defaultMessage="Your team has already used half your free automation runs. Try a higher limit free for {trialLength} days with {edition, select, premium {Premium} standard {Standard} other {Standard}}."
			description="Modal body text for the Automations variation of the upgrade modal."
			values={{ trialLength, edition }}
		/>
	);
}

// ─── Rovo ────────────────────────────────────────────────────────────────────

function RovoHeading({ count }: { count: number }) {
	return (
		<FormattedMessage
			id="growth-luna.jira-twcg-810-upgrade-free.modal.rovo.heading"
			defaultMessage="{count, plural, =0 {Stay across your team's tasks} one {Stay across your team's # task} other {Stay across your team's # tasks}}"
			description="Modal heading for the Rovo variation of the upgrade modal."
			values={{ count }}
		/>
	);
}

function RovoBody({ trialLength, edition }: { trialLength: number; edition: RecommendedEdition }) {
	return (
		<FormattedMessage
			id="growth-luna.jira-twcg-810-upgrade-free.modal.rovo.body"
			defaultMessage="Help your team move work in progress faster. Try Rovo AI free for {trialLength} days with {edition, select, premium {Premium} standard {Standard} other {Standard}}."
			description="Modal body text for the Rovo variation of the upgrade modal."
			values={{ trialLength, edition }}
		/>
	);
}

// ─── Feature rows ─────────────────────────────────────────────────────────────

const PLANS_FEATURES: ReadonlyArray<FeatureItem> = [
	{
		id: 'growth-luna.jira-twcg-810-upgrade-free.modal.plans.feature.timelines',
		Icon: RoadmapIcon,
		message: (
			<FormattedMessage
				id="growth-luna.jira-twcg-810-upgrade-free.modal.plans.feature.timelines"
				defaultMessage="Create one view of all your team's timelines"
				description="Feature bullet: Plans timelines"
			/>
		),
	},
	{
		id: 'growth-luna.jira-twcg-810-upgrade-free.modal.plans.feature.blockers',
		Icon: WorkItemsIcon,
		message: (
			<FormattedMessage
				id="growth-luna.jira-twcg-810-upgrade-free.modal.plans.feature.blockers"
				defaultMessage="Spot blockers early and keep things moving"
				description="Feature bullet: Plans blockers"
			/>
		),
	},
	{
		id: 'growth-luna.jira-twcg-810-upgrade-free.modal.plans.feature.goals',
		Icon: TeamsIcon,
		message: (
			<FormattedMessage
				id="growth-luna.jira-twcg-810-upgrade-free.modal.plans.feature.goals"
				defaultMessage="Help your team hit goals together"
				description="Feature bullet: Plans team goals"
			/>
		),
	},
];

const AUTOMATIONS_FEATURES: ReadonlyArray<FeatureItem> = [
	{
		id: 'growth-luna.jira-twcg-810-upgrade-free.modal.automations.feature.workflows',
		Icon: CheckCircleIcon,
		message: (
			<FormattedMessage
				id="growth-luna.jira-twcg-810-upgrade-free.modal.automations.feature.workflows"
				defaultMessage="Keep workflows moving and avoid interruptions"
				description="Feature bullet: Automations workflows"
			/>
		),
	},
	{
		id: 'growth-luna.jira-twcg-810-upgrade-free.modal.automations.feature.limit',
		Icon: AutomationIcon,
		message: (
			<FormattedMessage
				id="growth-luna.jira-twcg-810-upgrade-free.modal.automations.feature.limit"
				defaultMessage="Increase your limit to 1,700 monthly runs"
				description="Feature bullet: Automations run limit"
			/>
		),
	},
	{
		id: 'growth-luna.jira-twcg-810-upgrade-free.modal.automations.feature.rovo',
		Icon: RovoIcon,
		message: (
			<FormattedMessage
				id="growth-luna.jira-twcg-810-upgrade-free.modal.automations.feature.rovo"
				defaultMessage="Create new automations fast with Rovo AI"
				description="Feature bullet: Automations with Rovo AI"
			/>
		),
	},
];

const ROVO_FEATURES: ReadonlyArray<FeatureItem> = [
	{
		id: 'growth-luna.jira-twcg-810-upgrade-free.modal.rovo.feature.summaries',
		Icon: AiGenerativeTextSummaryIcon,
		message: (
			<FormattedMessage
				id="growth-luna.jira-twcg-810-upgrade-free.modal.rovo.feature.summaries"
				defaultMessage="Get instant summaries of what's happening"
				description="Feature bullet: Rovo AI summaries"
			/>
		),
	},
	{
		id: 'growth-luna.jira-twcg-810-upgrade-free.modal.rovo.feature.next-steps',
		Icon: RovoIcon,
		message: (
			<FormattedMessage
				id="growth-luna.jira-twcg-810-upgrade-free.modal.rovo.feature.next-steps"
				defaultMessage="Turn big work into clearer next steps"
				description="Feature bullet: Rovo next steps"
			/>
		),
	},
	{
		id: 'growth-luna.jira-twcg-810-upgrade-free.modal.rovo.feature.connect',
		Icon: WorkItemAddIcon,
		message: (
			<FormattedMessage
				id="growth-luna.jira-twcg-810-upgrade-free.modal.rovo.feature.connect"
				defaultMessage="Connect related and duplicate tasks automatically"
				description="Feature bullet: Rovo connect tasks"
			/>
		),
	},
];

export const MODAL_CONTENT: Record<RecommendedFeature, ModalVariantContent> = {
	plans: {
		HeadingMessage: PlansHeading,
		BodyMessage: PlansBody,
		features: PLANS_FEATURES,
	},
	automations: {
		HeadingMessage: AutomationsHeading,
		BodyMessage: AutomationsBody,
		features: AUTOMATIONS_FEATURES,
	},
	rovo: {
		HeadingMessage: RovoHeading,
		BodyMessage: RovoBody,
		features: ROVO_FEATURES,
	},
};
